// Primary Science Curriculum - Years 1-6
// Based on Vanuatu National Curriculum - Science
// 4 Strands: Living Things, Physical Science, Earth & Space, Scientific Inquiry

// ============================================
// YEAR 1 SCIENCE CURRICULUM
// Exploring Our World
// ============================================
export const scienceYear1 = {
  year: 1,
  subject: 'Science',
  totalWeeks: 40,
  periodsPerWeek: 3,
  terms: [
    {
      term: 'Term 1',
      weeks: 10,
      focus: 'Living Things Around Us',
      scheme: [
        {
          week: 1,
          topic: 'What is Science?',
          strand: 'Scientific Inquiry',
          learningOutcomes: [
            'Understand what scientists do',
            'Use senses to observe',
            'Ask questions about the world'
          ],
          activities: [
            'Nature walk observations',
            'Using our senses activity',
            'Question brainstorming'
          ],
          resources: ['Observation sheets', 'Magnifying glasses', 'Nature items'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Welcome to Science!',
              duration: '30 min',
              objectives: ['Understand that science is about exploring our world', 'Know that scientists ask questions'],
              introduction: 'Science helps us understand the world around us! Scientists are curious people who ask questions.',
              mainActivities: [
                { activity: 'What is Science?', duration: '10 min', description: 'Discuss what science means - studying the world' },
                { activity: 'Scientist Role Play', duration: '15 min', description: 'Pretend to be scientists, use magnifying glass' },
                { activity: 'Question Time', duration: '5 min', description: 'Share one question about nature' }
              ],
              materials: ['Magnifying glasses', 'Lab coats or aprons', 'Nature pictures'],
              assessment: 'Can explain what scientists do',
              homework: 'Find something interesting in nature to bring to class'
            },
            {
              day: 'Wednesday',
              title: 'Using Our Senses',
              duration: '30 min',
              objectives: ['Name the five senses', 'Use senses to observe objects'],
              introduction: 'Scientists use their senses to learn about things!',
              mainActivities: [
                { activity: 'Five Senses Song', duration: '10 min', description: 'Learn song about see, hear, smell, taste, touch' },
                { activity: 'Sense Stations', duration: '15 min', description: 'Rotate through stations using different senses' },
                { activity: 'Observation Share', duration: '5 min', description: 'Share what we observed' }
              ],
              materials: ['Items for each sense station', 'Recording sheets'],
              assessment: 'Can name and use five senses',
              homework: 'Use senses at home and tell family what you observed'
            },
            {
              day: 'Friday',
              title: 'Asking Good Questions',
              duration: '30 min',
              objectives: ['Ask questions about nature', 'Begin to wonder "why" and "how"'],
              introduction: 'Good scientists ask lots of questions!',
              mainActivities: [
                { activity: 'Mystery Object', duration: '10 min', description: 'Observe mystery object, ask questions about it' },
                { activity: 'Question Practice', duration: '15 min', description: 'Practice asking why and how questions' },
                { activity: 'Question Wall', duration: '5 min', description: 'Add questions to class question wall' }
              ],
              materials: ['Mystery objects', 'Question wall paper', 'Markers'],
              assessment: 'Can ask a question about nature',
              homework: 'Ask a family member a science question'
            }
          ]
        },
        {
          week: 2,
          topic: 'Living and Non-Living Things',
          strand: 'Living Things',
          learningOutcomes: [
            'Distinguish living from non-living things',
            'Identify characteristics of living things',
            'Sort objects into living/non-living'
          ],
          activities: ['Sorting activities', 'Nature walk', 'Classification games'],
          resources: ['Sorting cards', 'Collection bags', 'Classification charts']
        },
        {
          week: 3,
          topic: 'Plants in Our Environment',
          strand: 'Living Things',
          learningOutcomes: [
            'Identify common plants',
            'Name parts of a plant',
            'Understand plants are living things'
          ],
          activities: ['Plant walk', 'Plant part labeling', 'Plant observation'],
          resources: ['Plant samples', 'Plant diagrams', 'Magnifying glasses']
        },
        {
          week: 4,
          topic: 'What Plants Need',
          strand: 'Living Things',
          learningOutcomes: [
            'Identify what plants need to grow',
            'Understand plants need water, sunlight, soil',
            'Care for a class plant'
          ],
          activities: ['Plant experiment setup', 'Watering practice', 'Sunlight observation'],
          resources: ['Seeds', 'Pots', 'Soil', 'Watering cans']
        },
        {
          week: 5,
          topic: 'Animals Around Us',
          strand: 'Living Things',
          learningOutcomes: [
            'Identify common animals in Vanuatu',
            'Describe animal characteristics',
            'Understand animals are living things'
          ],
          activities: ['Animal identification', 'Animal sounds game', 'Animal drawings'],
          resources: ['Animal pictures', 'Animal sounds recordings', 'Drawing materials']
        },
        {
          week: 6,
          topic: 'Animal Needs',
          strand: 'Living Things',
          learningOutcomes: [
            'Identify what animals need',
            'Compare animal needs to plant needs',
            'Understand all living things have needs'
          ],
          activities: ['Needs comparison', 'Animal care discussion', 'Matching games'],
          resources: ['Needs charts', 'Animal pictures', 'Matching cards']
        },
        {
          week: 7,
          topic: 'Animal Homes',
          strand: 'Living Things',
          learningOutcomes: [
            'Identify where animals live',
            'Match animals to their homes',
            'Understand habitats concept'
          ],
          activities: ['Habitat matching', 'Home building craft', 'Habitat exploration'],
          resources: ['Habitat pictures', 'Craft materials', 'Matching games']
        },
        {
          week: 8,
          topic: 'Taking Care of Living Things',
          strand: 'Living Things',
          learningOutcomes: [
            'Understand importance of caring for nature',
            'Identify ways to help plants and animals',
            'Practice caring behaviors'
          ],
          activities: ['Garden care', 'Caring discussions', 'Pledge making'],
          resources: ['Garden tools', 'Pledge cards', 'Care posters']
        },
        {
          week: 9,
          topic: 'Living Things Review',
          strand: 'Living Things',
          learningOutcomes: [
            'Review living vs non-living',
            'Review plant and animal needs',
            'Demonstrate understanding'
          ],
          activities: ['Review games', 'Sorting activities', 'Assessment preparation'],
          resources: ['Review materials', 'Games', 'Sorting cards']
        },
        {
          week: 10,
          topic: 'Term 1 Assessment',
          strand: 'All Strands',
          learningOutcomes: [
            'Demonstrate knowledge of living things',
            'Show observation skills',
            'Complete assessment tasks'
          ],
          activities: ['Assessment activities', 'Portfolio review', 'Celebration'],
          resources: ['Assessment materials', 'Portfolios', 'Rewards']
        }
      ]
    },
    {
      term: 'Term 2',
      weeks: 10,
      focus: 'Our Bodies & Physical Science',
      scheme: [
        {
          week: 1,
          topic: 'Parts of Our Body',
          strand: 'Living Things',
          learningOutcomes: ['Name major body parts', 'Identify body part functions', 'Understand we are living things'],
          activities: ['Body part songs', 'Labeling activities', 'Movement games'],
          resources: ['Body diagrams', 'Songs', 'Labels']
        },
        {
          week: 2,
          topic: 'Our Five Senses',
          strand: 'Living Things',
          learningOutcomes: ['Identify five senses and organs', 'Use senses to explore', 'Describe sensory experiences'],
          activities: ['Sense exploration', 'Matching games', 'Sensory walks'],
          resources: ['Sense materials', 'Matching cards', 'Recording sheets']
        },
        {
          week: 3,
          topic: 'Staying Healthy',
          strand: 'Living Things',
          learningOutcomes: ['Understand basic health practices', 'Know importance of hygiene', 'Practice healthy habits'],
          activities: ['Hand washing practice', 'Health discussions', 'Healthy habits chart'],
          resources: ['Soap', 'Health posters', 'Chart materials']
        },
        {
          week: 4,
          topic: 'Push and Pull',
          strand: 'Physical Science',
          learningOutcomes: ['Understand push and pull forces', 'Identify push/pull in everyday life', 'Experiment with forces'],
          activities: ['Push/pull experiments', 'Force identification', 'Movement activities'],
          resources: ['Toys', 'Objects to push/pull', 'Recording sheets']
        },
        {
          week: 5,
          topic: 'Things That Move',
          strand: 'Physical Science',
          learningOutcomes: ['Observe how things move', 'Describe different movements', 'Understand force causes motion'],
          activities: ['Movement observations', 'Rolling experiments', 'Motion games'],
          resources: ['Balls', 'Ramps', 'Wheeled toys']
        },
        {
          week: 6,
          topic: 'Floating and Sinking',
          strand: 'Physical Science',
          learningOutcomes: ['Predict what floats/sinks', 'Test predictions', 'Observe results'],
          activities: ['Float/sink experiments', 'Prediction activities', 'Recording results'],
          resources: ['Water containers', 'Various objects', 'Recording sheets']
        },
        {
          week: 7,
          topic: 'Light and Dark',
          strand: 'Physical Science',
          learningOutcomes: ['Identify sources of light', 'Understand light and dark', 'Explore shadows'],
          activities: ['Light source hunt', 'Shadow play', 'Light experiments'],
          resources: ['Flashlights', 'Shadow puppets', 'Light sources']
        },
        {
          week: 8,
          topic: 'Sounds Around Us',
          strand: 'Physical Science',
          learningOutcomes: ['Identify different sounds', 'Understand sounds come from vibrations', 'Create sounds'],
          activities: ['Sound walks', 'Sound making', 'Sound sorting'],
          resources: ['Sound makers', 'Recording devices', 'Musical instruments']
        },
        {
          week: 9,
          topic: 'Physical Science Review',
          strand: 'Physical Science',
          learningOutcomes: ['Review forces and motion', 'Review light and sound', 'Demonstrate understanding'],
          activities: ['Review activities', 'Experiments', 'Games'],
          resources: ['Review materials', 'Experiment supplies', 'Games']
        },
        {
          week: 10,
          topic: 'Term 2 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Show body knowledge', 'Demonstrate physical science understanding', 'Complete assessments'],
          activities: ['Assessment tasks', 'Portfolio addition', 'Celebration'],
          resources: ['Assessment materials', 'Portfolios', 'Rewards']
        }
      ]
    },
    {
      term: 'Term 3',
      weeks: 10,
      focus: 'Earth & Weather',
      scheme: [
        {
          week: 1,
          topic: 'Our Earth',
          strand: 'Earth & Space',
          learningOutcomes: ['Know Earth is our planet', 'Identify land and water', 'Understand Earth is our home'],
          activities: ['Globe exploration', 'Land/water sorting', 'Earth drawings'],
          resources: ['Globe', 'Maps', 'Drawing materials']
        },
        {
          week: 2,
          topic: 'Soil and Rocks',
          strand: 'Earth & Space',
          learningOutcomes: ['Observe different soils', 'Identify rocks', 'Understand soil importance'],
          activities: ['Soil exploration', 'Rock collection', 'Sorting activities'],
          resources: ['Soil samples', 'Rocks', 'Magnifying glasses']
        },
        {
          week: 3,
          topic: 'Water in Our World',
          strand: 'Earth & Space',
          learningOutcomes: ['Identify water sources', 'Understand water importance', 'Know water uses'],
          activities: ['Water source identification', 'Water use discussion', 'Conservation talk'],
          resources: ['Water pictures', 'Water samples', 'Charts']
        },
        {
          week: 4,
          topic: 'Weather Observation',
          strand: 'Earth & Space',
          learningOutcomes: ['Observe daily weather', 'Describe weather conditions', 'Record weather'],
          activities: ['Weather watching', 'Weather recording', 'Weather vocabulary'],
          resources: ['Weather chart', 'Recording sheets', 'Weather symbols']
        },
        {
          week: 5,
          topic: 'Sunny Days',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand sunny weather', 'Know sun gives heat and light', 'Practice sun safety'],
          activities: ['Sun observations', 'Shadow activities', 'Sun safety discussion'],
          resources: ['Sun pictures', 'Shadow materials', 'Sun safety posters']
        },
        {
          week: 6,
          topic: 'Rainy Days',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand rain', 'Know rain helps plants grow', 'Observe rain effects'],
          activities: ['Rain observations', 'Rain gauge', 'Rain drawings'],
          resources: ['Rain gauge', 'Recording sheets', 'Drawing materials']
        },
        {
          week: 7,
          topic: 'Wind',
          strand: 'Earth & Space',
          learningOutcomes: ['Observe wind effects', 'Understand wind is moving air', 'Measure wind'],
          activities: ['Wind observations', 'Wind experiments', 'Windsock making'],
          resources: ['Windsock materials', 'Light objects', 'Recording sheets']
        },
        {
          week: 8,
          topic: 'Seasons in Vanuatu',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand wet and dry seasons', 'Identify seasonal changes', 'Connect seasons to daily life'],
          activities: ['Season discussions', 'Season sorting', 'Season calendar'],
          resources: ['Season pictures', 'Calendar', 'Sorting materials']
        },
        {
          week: 9,
          topic: 'Earth & Weather Review',
          strand: 'Earth & Space',
          learningOutcomes: ['Review earth concepts', 'Review weather types', 'Demonstrate understanding'],
          activities: ['Review games', 'Weather activities', 'Portfolio work'],
          resources: ['Review materials', 'Games', 'Portfolio supplies']
        },
        {
          week: 10,
          topic: 'Term 3 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Show earth knowledge', 'Demonstrate weather understanding', 'Complete assessments'],
          activities: ['Assessment tasks', 'Weather observation', 'Celebration'],
          resources: ['Assessment materials', 'Weather tools', 'Rewards']
        }
      ]
    },
    {
      term: 'Term 4',
      weeks: 10,
      focus: 'Materials & Environment',
      scheme: [
        {
          week: 1,
          topic: 'Different Materials',
          strand: 'Physical Science',
          learningOutcomes: ['Identify common materials', 'Describe material properties', 'Sort by material type'],
          activities: ['Material hunt', 'Property descriptions', 'Sorting activities'],
          resources: ['Various materials', 'Sorting mats', 'Labels']
        },
        {
          week: 2,
          topic: 'Hard and Soft',
          strand: 'Physical Science',
          learningOutcomes: ['Distinguish hard and soft materials', 'Sort materials by hardness', 'Describe textures'],
          activities: ['Texture exploration', 'Sorting activities', 'Touch games'],
          resources: ['Hard/soft objects', 'Sorting mats', 'Recording sheets']
        },
        {
          week: 3,
          topic: 'Rough and Smooth',
          strand: 'Physical Science',
          learningOutcomes: ['Identify rough and smooth surfaces', 'Describe textures', 'Compare materials'],
          activities: ['Texture rubbings', 'Sorting activities', 'Comparison games'],
          resources: ['Textured materials', 'Paper', 'Crayons']
        },
        {
          week: 4,
          topic: 'Natural and Made Materials',
          strand: 'Physical Science',
          learningOutcomes: ['Distinguish natural from made materials', 'Identify sources of materials', 'Sort materials'],
          activities: ['Material sorting', 'Source identification', 'Classification games'],
          resources: ['Natural materials', 'Made objects', 'Sorting cards']
        },
        {
          week: 5,
          topic: 'Our Environment',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand environment concept', 'Identify parts of our environment', 'Appreciate our surroundings'],
          activities: ['Environment walk', 'Drawing activities', 'Discussion'],
          resources: ['Drawing materials', 'Environment pictures', 'Observation sheets']
        },
        {
          week: 6,
          topic: 'Taking Care of Our Environment',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand keeping environment clean', 'Know about rubbish disposal', 'Practice caring behaviors'],
          activities: ['Clean-up activities', 'Rubbish sorting', 'Pledge making'],
          resources: ['Cleaning supplies', 'Rubbish bins', 'Pledge cards']
        },
        {
          week: 7,
          topic: 'Reduce, Reuse, Recycle',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand reducing waste', 'Know about reusing items', 'Learn basic recycling'],
          activities: ['3Rs introduction', 'Reuse crafts', 'Sorting activities'],
          resources: ['Recyclable items', 'Craft supplies', 'Sorting bins']
        },
        {
          week: 8,
          topic: 'Year 1 Science Review',
          strand: 'All Strands',
          learningOutcomes: ['Review living things', 'Review physical science', 'Review earth science'],
          activities: ['Comprehensive review', 'Games', 'Portfolio completion'],
          resources: ['Review materials', 'Games', 'Portfolios']
        },
        {
          week: 9,
          topic: 'Science Skills Review',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Review observation skills', 'Review questioning skills', 'Demonstrate scientific thinking'],
          activities: ['Skills practice', 'Observation activities', 'Question review'],
          resources: ['Observation materials', 'Question cards', 'Recording sheets']
        },
        {
          week: 10,
          topic: 'Year 1 Final Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Complete final assessment', 'Celebrate science learning', 'Prepare for Year 2'],
          activities: ['Final assessment', 'Awards', 'Celebration'],
          resources: ['Assessment materials', 'Certificates', 'Celebration supplies']
        }
      ]
    }
  ]
};

// ============================================
// YEAR 2 SCIENCE CURRICULUM
// Discovering Patterns
// ============================================
export const scienceYear2 = {
  year: 2,
  subject: 'Science',
  totalWeeks: 40,
  periodsPerWeek: 3,
  terms: [
    {
      term: 'Term 1',
      weeks: 10,
      focus: 'Plant and Animal Life Cycles',
      scheme: [
        {
          week: 1,
          topic: 'Plant Life Cycle',
          strand: 'Living Things',
          learningOutcomes: ['Understand plants grow from seeds', 'Identify life cycle stages', 'Observe seed germination'],
          activities: ['Seed planting', 'Life cycle sequencing', 'Observation journals'],
          resources: ['Seeds', 'Pots', 'Soil', 'Life cycle charts']
        },
        {
          week: 2,
          topic: 'Growing Plants',
          strand: 'Living Things',
          learningOutcomes: ['Observe plant growth', 'Record growth changes', 'Understand growth needs'],
          activities: ['Plant observation', 'Measurement', 'Growth recording'],
          resources: ['Growing plants', 'Rulers', 'Recording sheets']
        },
        {
          week: 3,
          topic: 'Parts of Plants and Their Jobs',
          strand: 'Living Things',
          learningOutcomes: ['Identify plant parts', 'Understand function of each part', 'Relate structure to function'],
          activities: ['Plant dissection', 'Part labeling', 'Function matching'],
          resources: ['Plant samples', 'Diagrams', 'Matching cards']
        },
        {
          week: 4,
          topic: 'Animal Life Cycles',
          strand: 'Living Things',
          learningOutcomes: ['Understand animals have life cycles', 'Compare different life cycles', 'Sequence life cycle stages'],
          activities: ['Life cycle exploration', 'Sequencing activities', 'Comparison charts'],
          resources: ['Life cycle cards', 'Sequencing materials', 'Comparison charts']
        },
        {
          week: 5,
          topic: 'Butterfly Life Cycle',
          strand: 'Living Things',
          learningOutcomes: ['Learn butterfly metamorphosis', 'Sequence butterfly life cycle', 'Understand transformation'],
          activities: ['Metamorphosis study', 'Life cycle art', 'Sequencing'],
          resources: ['Butterfly pictures', 'Art materials', 'Life cycle cards']
        },
        {
          week: 6,
          topic: 'Frog Life Cycle',
          strand: 'Living Things',
          learningOutcomes: ['Learn frog life cycle', 'Compare to butterfly', 'Understand amphibian development'],
          activities: ['Frog life cycle study', 'Comparison activities', 'Drawings'],
          resources: ['Frog life cycle materials', 'Comparison charts', 'Art supplies']
        },
        {
          week: 7,
          topic: 'Chicken Life Cycle',
          strand: 'Living Things',
          learningOutcomes: ['Learn chicken development', 'Understand egg to chick', 'Compare to other cycles'],
          activities: ['Chicken cycle study', 'Egg observation', 'Comparison'],
          resources: ['Life cycle materials', 'Eggs', 'Pictures']
        },
        {
          week: 8,
          topic: 'Human Growth',
          strand: 'Living Things',
          learningOutcomes: ['Understand human growth stages', 'Compare baby to adult', 'Know humans are living things'],
          activities: ['Growth comparison', 'Timeline creation', 'Discussion'],
          resources: ['Growth pictures', 'Timeline materials', 'Measuring tools']
        },
        {
          week: 9,
          topic: 'Life Cycles Review',
          strand: 'Living Things',
          learningOutcomes: ['Compare all life cycles studied', 'Identify common patterns', 'Demonstrate understanding'],
          activities: ['Comparison activities', 'Pattern identification', 'Review games'],
          resources: ['Review materials', 'Comparison charts', 'Games']
        },
        {
          week: 10,
          topic: 'Term 1 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate life cycle knowledge', 'Show observation skills', 'Complete assessments'],
          activities: ['Assessment tasks', 'Portfolio review', 'Celebration'],
          resources: ['Assessment materials', 'Portfolios', 'Rewards']
        }
      ]
    },
    {
      term: 'Term 2',
      weeks: 10,
      focus: 'Habitats & Adaptations',
      scheme: [
        {
          week: 1,
          topic: 'What is a Habitat?',
          strand: 'Living Things',
          learningOutcomes: ['Define habitat', 'Identify different habitats', 'Understand habitat provides needs'],
          activities: ['Habitat introduction', 'Habitat identification', 'Needs discussion'],
          resources: ['Habitat pictures', 'Definition cards', 'Discussion guides']
        },
        {
          week: 2,
          topic: 'Forest Habitats',
          strand: 'Living Things',
          learningOutcomes: ['Identify forest habitat features', 'Know forest animals and plants', 'Understand forest ecosystem'],
          activities: ['Forest exploration', 'Animal/plant identification', 'Ecosystem discussion'],
          resources: ['Forest pictures', 'Animal cards', 'Plant samples']
        },
        {
          week: 3,
          topic: 'Ocean Habitats',
          strand: 'Living Things',
          learningOutcomes: ['Identify ocean habitat features', 'Know marine animals', 'Understand ocean life'],
          activities: ['Ocean exploration', 'Marine life study', 'Habitat creation'],
          resources: ['Ocean pictures', 'Marine animal cards', 'Craft materials']
        },
        {
          week: 4,
          topic: 'Freshwater Habitats',
          strand: 'Living Things',
          learningOutcomes: ['Identify freshwater habitats', 'Know freshwater animals', 'Compare to ocean'],
          activities: ['River/pond study', 'Animal identification', 'Comparison'],
          resources: ['Freshwater pictures', 'Animal cards', 'Comparison charts']
        },
        {
          week: 5,
          topic: 'Vanuatu Habitats',
          strand: 'Living Things',
          learningOutcomes: ['Identify local habitats', 'Know Vanuatu plants and animals', 'Appreciate local biodiversity'],
          activities: ['Local habitat walk', 'Native species study', 'Drawing activities'],
          resources: ['Local guides', 'Drawing materials', 'Observation sheets']
        },
        {
          week: 6,
          topic: 'Animal Adaptations',
          strand: 'Living Things',
          learningOutcomes: ['Understand adaptation concept', 'Identify animal adaptations', 'Connect adaptation to survival'],
          activities: ['Adaptation study', 'Matching games', 'Discussion'],
          resources: ['Adaptation pictures', 'Matching cards', 'Examples']
        },
        {
          week: 7,
          topic: 'Plant Adaptations',
          strand: 'Living Things',
          learningOutcomes: ['Identify plant adaptations', 'Understand how plants survive', 'Compare adaptations'],
          activities: ['Plant observation', 'Adaptation identification', 'Comparison'],
          resources: ['Plant samples', 'Pictures', 'Comparison charts']
        },
        {
          week: 8,
          topic: 'Protecting Habitats',
          strand: 'Living Things',
          learningOutcomes: ['Understand habitat threats', 'Know conservation importance', 'Learn protection methods'],
          activities: ['Threat discussion', 'Conservation activities', 'Action planning'],
          resources: ['Conservation materials', 'Action cards', 'Pledge sheets']
        },
        {
          week: 9,
          topic: 'Habitats Review',
          strand: 'Living Things',
          learningOutcomes: ['Review all habitats studied', 'Compare habitat features', 'Demonstrate understanding'],
          activities: ['Review activities', 'Comparison games', 'Portfolio work'],
          resources: ['Review materials', 'Games', 'Portfolios']
        },
        {
          week: 10,
          topic: 'Term 2 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Show habitat knowledge', 'Demonstrate adaptation understanding', 'Complete assessments'],
          activities: ['Assessment tasks', 'Habitat projects', 'Celebration'],
          resources: ['Assessment materials', 'Project supplies', 'Rewards']
        }
      ]
    },
    {
      term: 'Term 3',
      weeks: 10,
      focus: 'Matter & Energy',
      scheme: [
        {
          week: 1,
          topic: 'States of Matter: Solids',
          strand: 'Physical Science',
          learningOutcomes: ['Identify solid properties', 'Give examples of solids', 'Describe solid characteristics'],
          activities: ['Solid exploration', 'Property identification', 'Sorting activities'],
          resources: ['Solid objects', 'Sorting mats', 'Recording sheets']
        },
        {
          week: 2,
          topic: 'States of Matter: Liquids',
          strand: 'Physical Science',
          learningOutcomes: ['Identify liquid properties', 'Give examples of liquids', 'Compare to solids'],
          activities: ['Liquid exploration', 'Pouring experiments', 'Comparison activities'],
          resources: ['Various liquids', 'Containers', 'Comparison charts']
        },
        {
          week: 3,
          topic: 'States of Matter: Gases',
          strand: 'Physical Science',
          learningOutcomes: ['Understand gases exist', 'Know air is a gas', 'Observe gas properties'],
          activities: ['Air experiments', 'Balloon activities', 'Gas observation'],
          resources: ['Balloons', 'Air pumps', 'Experiment materials']
        },
        {
          week: 4,
          topic: 'Changing States',
          strand: 'Physical Science',
          learningOutcomes: ['Observe melting', 'Observe freezing', 'Understand state changes'],
          activities: ['Melting experiments', 'Freezing experiments', 'Observation recording'],
          resources: ['Ice', 'Heat source', 'Recording sheets']
        },
        {
          week: 5,
          topic: 'Heat Energy',
          strand: 'Physical Science',
          learningOutcomes: ['Understand heat as energy', 'Identify heat sources', 'Observe heat effects'],
          activities: ['Heat source identification', 'Temperature observation', 'Heat experiments'],
          resources: ['Thermometers', 'Heat sources', 'Recording sheets']
        },
        {
          week: 6,
          topic: 'Light Energy',
          strand: 'Physical Science',
          learningOutcomes: ['Understand light as energy', 'Identify light sources', 'Explore shadow formation'],
          activities: ['Light source hunt', 'Shadow experiments', 'Light exploration'],
          resources: ['Flashlights', 'Light sources', 'Shadow materials']
        },
        {
          week: 7,
          topic: 'Sound Energy',
          strand: 'Physical Science',
          learningOutcomes: ['Understand sound as energy', 'Know sound travels', 'Create different sounds'],
          activities: ['Sound experiments', 'Vibration observation', 'Sound creation'],
          resources: ['Sound makers', 'Musical instruments', 'Experiment materials']
        },
        {
          week: 8,
          topic: 'Energy in Daily Life',
          strand: 'Physical Science',
          learningOutcomes: ['Identify energy use at home', 'Understand energy sources', 'Know energy importance'],
          activities: ['Energy audit', 'Source identification', 'Discussion'],
          resources: ['Energy pictures', 'Audit sheets', 'Discussion guides']
        },
        {
          week: 9,
          topic: 'Matter & Energy Review',
          strand: 'Physical Science',
          learningOutcomes: ['Review states of matter', 'Review energy types', 'Demonstrate understanding'],
          activities: ['Review experiments', 'Games', 'Portfolio work'],
          resources: ['Review materials', 'Experiment supplies', 'Portfolios']
        },
        {
          week: 10,
          topic: 'Term 3 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Show matter knowledge', 'Demonstrate energy understanding', 'Complete assessments'],
          activities: ['Assessment tasks', 'Science experiments', 'Celebration'],
          resources: ['Assessment materials', 'Experiment supplies', 'Rewards']
        }
      ]
    },
    {
      term: 'Term 4',
      weeks: 10,
      focus: 'Earth Changes & Scientific Inquiry',
      scheme: [
        {
          week: 1,
          topic: 'Slow Earth Changes',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand erosion', 'Know weathering effects', 'Observe slow changes'],
          activities: ['Erosion observation', 'Weathering examples', 'Change documentation'],
          resources: ['Rock samples', 'Pictures', 'Observation sheets']
        },
        {
          week: 2,
          topic: 'Fast Earth Changes',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand earthquakes', 'Know about volcanic eruptions', 'Learn about cyclones'],
          activities: ['Natural disaster study', 'Safety discussion', 'Model making'],
          resources: ['Disaster pictures', 'Safety guides', 'Model materials']
        },
        {
          week: 3,
          topic: 'Day and Night',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand day and night cycle', 'Know Earth rotates', 'Connect to daily life'],
          activities: ['Day/night exploration', 'Earth rotation model', 'Schedule connection'],
          resources: ['Globe', 'Flashlight', 'Schedule charts']
        },
        {
          week: 4,
          topic: 'The Sun and Moon',
          strand: 'Earth & Space',
          learningOutcomes: ['Know about the sun', 'Know about the moon', 'Observe the sky'],
          activities: ['Sky observation', 'Sun/moon comparison', 'Drawing activities'],
          resources: ['Sky pictures', 'Observation sheets', 'Drawing materials']
        },
        {
          week: 5,
          topic: 'Scientific Questions',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Ask testable questions', 'Distinguish testable questions', 'Plan simple investigations'],
          activities: ['Question practice', 'Investigation planning', 'Testing discussions'],
          resources: ['Question cards', 'Planning sheets', 'Investigation guides']
        },
        {
          week: 6,
          topic: 'Making Predictions',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Make predictions', 'Base predictions on observations', 'Test predictions'],
          activities: ['Prediction practice', 'Testing activities', 'Result recording'],
          resources: ['Prediction sheets', 'Testing materials', 'Recording sheets']
        },
        {
          week: 7,
          topic: 'Simple Experiments',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Conduct simple experiments', 'Follow procedure', 'Record results'],
          activities: ['Experiment practice', 'Procedure following', 'Result recording'],
          resources: ['Experiment materials', 'Procedure cards', 'Recording sheets']
        },
        {
          week: 8,
          topic: 'Drawing Conclusions',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Analyze results', 'Draw simple conclusions', 'Share findings'],
          activities: ['Result analysis', 'Conclusion writing', 'Sharing presentations'],
          resources: ['Analysis guides', 'Conclusion frames', 'Presentation materials']
        },
        {
          week: 9,
          topic: 'Year 2 Science Review',
          strand: 'All Strands',
          learningOutcomes: ['Review all Year 2 topics', 'Connect concepts', 'Demonstrate understanding'],
          activities: ['Comprehensive review', 'Connection activities', 'Portfolio completion'],
          resources: ['Review materials', 'Connection charts', 'Portfolios']
        },
        {
          week: 10,
          topic: 'Year 2 Final Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Complete final assessment', 'Celebrate science learning', 'Prepare for Year 3'],
          activities: ['Final assessment', 'Awards', 'Celebration'],
          resources: ['Assessment materials', 'Certificates', 'Celebration supplies']
        }
      ]
    }
  ]
};

// ============================================
// YEAR 3 SCIENCE CURRICULUM
// Understanding Systems
// ============================================
export const scienceYear3 = {
  year: 3,
  subject: 'Science',
  totalWeeks: 40,
  periodsPerWeek: 4,
  terms: [
    {
      term: 'Term 1',
      weeks: 10,
      focus: 'Body Systems & Health',
      scheme: [
        {
          week: 1,
          topic: 'Skeletal System',
          strand: 'Living Things',
          learningOutcomes: ['Identify major bones', 'Understand skeleton function', 'Know bones give structure'],
          activities: ['Skeleton study', 'Bone identification', 'Model building'],
          resources: ['Skeleton model', 'Bone diagrams', 'Building materials']
        },
        {
          week: 2,
          topic: 'Muscular System',
          strand: 'Living Things',
          learningOutcomes: ['Understand muscle function', 'Know muscles move bones', 'Identify major muscles'],
          activities: ['Muscle exploration', 'Movement activities', 'Muscle identification'],
          resources: ['Muscle diagrams', 'Activity cards', 'Identification charts']
        },
        {
          week: 3,
          topic: 'Digestive System',
          strand: 'Living Things',
          learningOutcomes: ['Understand digestion process', 'Know digestive organs', 'Trace food through system'],
          activities: ['Digestion study', 'Organ identification', 'Process tracing'],
          resources: ['Digestive system diagrams', 'Process cards', 'Food journey materials']
        },
        {
          week: 4,
          topic: 'Respiratory System',
          strand: 'Living Things',
          learningOutcomes: ['Understand breathing process', 'Know respiratory organs', 'Connect to oxygen need'],
          activities: ['Breathing exploration', 'Lung model', 'Oxygen discussion'],
          resources: ['Respiratory diagrams', 'Model materials', 'Discussion guides']
        },
        {
          week: 5,
          topic: 'Circulatory System',
          strand: 'Living Things',
          learningOutcomes: ['Understand heart function', 'Know blood carries nutrients', 'Feel pulse'],
          activities: ['Heart study', 'Pulse finding', 'Blood flow tracing'],
          resources: ['Heart diagrams', 'Pulse guides', 'Flow charts']
        },
        {
          week: 6,
          topic: 'Nutrition and Health',
          strand: 'Living Things',
          learningOutcomes: ['Understand food groups', 'Know balanced diet importance', 'Plan healthy meals'],
          activities: ['Food group sorting', 'Meal planning', 'Nutrition discussion'],
          resources: ['Food group materials', 'Planning sheets', 'Nutrition guides']
        },
        {
          week: 7,
          topic: 'Exercise and Health',
          strand: 'Living Things',
          learningOutcomes: ['Understand exercise benefits', 'Know exercise affects body systems', 'Plan exercise activities'],
          activities: ['Exercise exploration', 'Body response observation', 'Activity planning'],
          resources: ['Exercise materials', 'Response recording sheets', 'Planning guides']
        },
        {
          week: 8,
          topic: 'Disease Prevention',
          strand: 'Living Things',
          learningOutcomes: ['Understand germs cause disease', 'Know prevention methods', 'Practice healthy habits'],
          activities: ['Germ study', 'Prevention discussion', 'Habit practice'],
          resources: ['Germ materials', 'Prevention posters', 'Habit charts']
        },
        {
          week: 9,
          topic: 'Body Systems Review',
          strand: 'Living Things',
          learningOutcomes: ['Review all body systems', 'Connect system functions', 'Demonstrate understanding'],
          activities: ['System review', 'Connection activities', 'Games'],
          resources: ['Review materials', 'Connection charts', 'Games']
        },
        {
          week: 10,
          topic: 'Term 1 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate body system knowledge', 'Show health understanding', 'Complete assessments'],
          activities: ['Assessment tasks', 'Body system projects', 'Celebration'],
          resources: ['Assessment materials', 'Project supplies', 'Rewards']
        }
      ]
    },
    {
      term: 'Term 2',
      weeks: 10,
      focus: 'Ecosystems & Food Chains',
      scheme: [
        {
          week: 1,
          topic: 'Ecosystem Introduction',
          strand: 'Living Things',
          learningOutcomes: ['Define ecosystem', 'Identify ecosystem components', 'Understand living and non-living interactions'],
          activities: ['Ecosystem exploration', 'Component identification', 'Interaction discussion'],
          resources: ['Ecosystem pictures', 'Component cards', 'Discussion guides']
        },
        {
          week: 2,
          topic: 'Producers',
          strand: 'Living Things',
          learningOutcomes: ['Understand producer role', 'Know plants make food', 'Identify producers'],
          activities: ['Producer study', 'Photosynthesis introduction', 'Producer identification'],
          resources: ['Plant materials', 'Photosynthesis diagrams', 'Identification activities']
        },
        {
          week: 3,
          topic: 'Consumers',
          strand: 'Living Things',
          learningOutcomes: ['Understand consumer types', 'Know herbivores, carnivores, omnivores', 'Classify animals'],
          activities: ['Consumer classification', 'Diet study', 'Sorting activities'],
          resources: ['Animal cards', 'Diet charts', 'Sorting materials']
        },
        {
          week: 4,
          topic: 'Decomposers',
          strand: 'Living Things',
          learningOutcomes: ['Understand decomposer role', 'Know decomposers recycle', 'Identify decomposers'],
          activities: ['Decomposition observation', 'Role discussion', 'Decomposer identification'],
          resources: ['Decomposition materials', 'Role cards', 'Identification activities']
        },
        {
          week: 5,
          topic: 'Food Chains',
          strand: 'Living Things',
          learningOutcomes: ['Understand food chain concept', 'Create simple food chains', 'Trace energy flow'],
          activities: ['Food chain creation', 'Energy flow tracing', 'Chain games'],
          resources: ['Food chain materials', 'Energy flow charts', 'Games']
        },
        {
          week: 6,
          topic: 'Food Webs',
          strand: 'Living Things',
          learningOutcomes: ['Understand food webs', 'Connect multiple food chains', 'Analyze web relationships'],
          activities: ['Web creation', 'Chain connection', 'Relationship analysis'],
          resources: ['Web materials', 'Connection tools', 'Analysis guides']
        },
        {
          week: 7,
          topic: 'Ecosystem Balance',
          strand: 'Living Things',
          learningOutcomes: ['Understand ecosystem balance', 'Know what disrupts balance', 'Predict changes'],
          activities: ['Balance exploration', 'Disruption scenarios', 'Prediction activities'],
          resources: ['Balance materials', 'Scenario cards', 'Prediction sheets']
        },
        {
          week: 8,
          topic: 'Local Ecosystems',
          strand: 'Living Things',
          learningOutcomes: ['Study local ecosystems', 'Identify local food chains', 'Appreciate local biodiversity'],
          activities: ['Local ecosystem walk', 'Food chain identification', 'Biodiversity appreciation'],
          resources: ['Local guides', 'Identification materials', 'Recording sheets']
        },
        {
          week: 9,
          topic: 'Ecosystem Review',
          strand: 'Living Things',
          learningOutcomes: ['Review ecosystem concepts', 'Connect all learning', 'Demonstrate understanding'],
          activities: ['Review activities', 'Connection games', 'Portfolio work'],
          resources: ['Review materials', 'Games', 'Portfolios']
        },
        {
          week: 10,
          topic: 'Term 2 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Show ecosystem knowledge', 'Demonstrate food chain/web understanding', 'Complete assessments'],
          activities: ['Assessment tasks', 'Ecosystem projects', 'Celebration'],
          resources: ['Assessment materials', 'Project supplies', 'Rewards']
        }
      ]
    },
    {
      term: 'Term 3',
      weeks: 10,
      focus: 'Forces & Motion',
      scheme: [
        {
          week: 1,
          topic: 'Forces Introduction',
          strand: 'Physical Science',
          learningOutcomes: ['Define force', 'Identify push and pull forces', 'Observe force effects'],
          activities: ['Force exploration', 'Push/pull activities', 'Effect observation'],
          resources: ['Force materials', 'Activity cards', 'Observation sheets']
        },
        {
          week: 2,
          topic: 'Gravity',
          strand: 'Physical Science',
          learningOutcomes: ['Understand gravity', 'Know gravity pulls down', 'Observe gravity effects'],
          activities: ['Gravity experiments', 'Drop tests', 'Effect observation'],
          resources: ['Drop materials', 'Experiment guides', 'Recording sheets']
        },
        {
          week: 3,
          topic: 'Friction',
          strand: 'Physical Science',
          learningOutcomes: ['Understand friction', 'Know friction slows things', 'Test friction'],
          activities: ['Friction experiments', 'Surface testing', 'Result recording'],
          resources: ['Various surfaces', 'Test materials', 'Recording sheets']
        },
        {
          week: 4,
          topic: 'Motion',
          strand: 'Physical Science',
          learningOutcomes: ['Describe motion types', 'Measure motion', 'Understand speed'],
          activities: ['Motion observation', 'Speed measurement', 'Motion description'],
          resources: ['Moving objects', 'Measuring tools', 'Recording sheets']
        },
        {
          week: 5,
          topic: 'Simple Machines: Lever',
          strand: 'Physical Science',
          learningOutcomes: ['Understand lever function', 'Identify lever parts', 'Use levers'],
          activities: ['Lever exploration', 'Part identification', 'Lever use'],
          resources: ['Lever materials', 'Identification guides', 'Activity cards']
        },
        {
          week: 6,
          topic: 'Simple Machines: Wheel and Axle',
          strand: 'Physical Science',
          learningOutcomes: ['Understand wheel and axle', 'Know how they help', 'Identify examples'],
          activities: ['Wheel exploration', 'Function study', 'Example identification'],
          resources: ['Wheel materials', 'Function guides', 'Example pictures']
        },
        {
          week: 7,
          topic: 'Simple Machines: Inclined Plane',
          strand: 'Physical Science',
          learningOutcomes: ['Understand inclined plane', 'Know it reduces effort', 'Use inclined planes'],
          activities: ['Ramp experiments', 'Effort comparison', 'Application'],
          resources: ['Ramp materials', 'Comparison activities', 'Recording sheets']
        },
        {
          week: 8,
          topic: 'Simple Machines: Pulley',
          strand: 'Physical Science',
          learningOutcomes: ['Understand pulley function', 'Know pulleys lift', 'Use pulleys'],
          activities: ['Pulley exploration', 'Lifting experiments', 'Pulley use'],
          resources: ['Pulley materials', 'Experiment guides', 'Activity cards']
        },
        {
          week: 9,
          topic: 'Forces & Machines Review',
          strand: 'Physical Science',
          learningOutcomes: ['Review force concepts', 'Review simple machines', 'Demonstrate understanding'],
          activities: ['Review activities', 'Machine games', 'Portfolio work'],
          resources: ['Review materials', 'Games', 'Portfolios']
        },
        {
          week: 10,
          topic: 'Term 3 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Show forces knowledge', 'Demonstrate machine understanding', 'Complete assessments'],
          activities: ['Assessment tasks', 'Machine projects', 'Celebration'],
          resources: ['Assessment materials', 'Project supplies', 'Rewards']
        }
      ]
    },
    {
      term: 'Term 4',
      weeks: 10,
      focus: 'Earth Resources & Conservation',
      scheme: [
        {
          week: 1,
          topic: 'Natural Resources',
          strand: 'Earth & Space',
          learningOutcomes: ['Define natural resources', 'Identify resource types', 'Know resource importance'],
          activities: ['Resource identification', 'Type classification', 'Importance discussion'],
          resources: ['Resource materials', 'Classification cards', 'Discussion guides']
        },
        {
          week: 2,
          topic: 'Renewable Resources',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand renewable resources', 'Identify examples', 'Know renewability'],
          activities: ['Renewable study', 'Example identification', 'Renewal discussion'],
          resources: ['Renewable materials', 'Example cards', 'Discussion guides']
        },
        {
          week: 3,
          topic: 'Non-Renewable Resources',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand non-renewable resources', 'Identify examples', 'Know conservation importance'],
          activities: ['Non-renewable study', 'Example identification', 'Conservation discussion'],
          resources: ['Non-renewable materials', 'Example cards', 'Discussion guides']
        },
        {
          week: 4,
          topic: 'Water Resources',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand water cycle', 'Know water sources', 'Learn water conservation'],
          activities: ['Water cycle study', 'Source identification', 'Conservation planning'],
          resources: ['Water cycle materials', 'Source pictures', 'Conservation guides']
        },
        {
          week: 5,
          topic: 'Soil as a Resource',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand soil importance', 'Know soil types', 'Learn soil conservation'],
          activities: ['Soil study', 'Type identification', 'Conservation activities'],
          resources: ['Soil samples', 'Type guides', 'Conservation materials']
        },
        {
          week: 6,
          topic: 'Pollution',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand pollution types', 'Know pollution effects', 'Learn prevention'],
          activities: ['Pollution study', 'Effect discussion', 'Prevention planning'],
          resources: ['Pollution materials', 'Effect pictures', 'Prevention guides']
        },
        {
          week: 7,
          topic: 'Conservation Actions',
          strand: 'Earth & Space',
          learningOutcomes: ['Know conservation methods', 'Plan conservation actions', 'Practice conservation'],
          activities: ['Method study', 'Action planning', 'Practice activities'],
          resources: ['Conservation guides', 'Planning sheets', 'Activity materials']
        },
        {
          week: 8,
          topic: 'Scientific Investigation',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Design investigations', 'Control variables', 'Collect data'],
          activities: ['Investigation design', 'Variable control', 'Data collection'],
          resources: ['Design guides', 'Variable materials', 'Data sheets']
        },
        {
          week: 9,
          topic: 'Year 3 Science Review',
          strand: 'All Strands',
          learningOutcomes: ['Review all Year 3 topics', 'Connect concepts', 'Demonstrate understanding'],
          activities: ['Comprehensive review', 'Connection activities', 'Portfolio completion'],
          resources: ['Review materials', 'Connection tools', 'Portfolios']
        },
        {
          week: 10,
          topic: 'Year 3 Final Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Complete final assessment', 'Celebrate science learning', 'Prepare for Year 4'],
          activities: ['Final assessment', 'Awards', 'Celebration'],
          resources: ['Assessment materials', 'Certificates', 'Celebration supplies']
        }
      ]
    }
  ]
};

// ============================================
// YEAR 4 SCIENCE CURRICULUM
// Investigating Our World
// ============================================
export const scienceYear4 = {
  year: 4,
  subject: 'Science',
  totalWeeks: 40,
  periodsPerWeek: 4,
  terms: [
    {
      term: 'Term 1',
      weeks: 10,
      focus: 'Classification & Diversity',
      scheme: [
        {
          week: 1,
          topic: 'Classifying Living Things',
          strand: 'Living Things',
          learningOutcomes: ['Understand classification purpose', 'Use classification keys', 'Group organisms'],
          activities: ['Classification introduction', 'Key use', 'Grouping activities'],
          resources: ['Classification keys', 'Organism cards', 'Grouping materials']
        },
        {
          week: 2,
          topic: 'Vertebrates',
          strand: 'Living Things',
          learningOutcomes: ['Define vertebrates', 'Identify vertebrate groups', 'Know characteristics'],
          activities: ['Vertebrate study', 'Group identification', 'Characteristic matching'],
          resources: ['Vertebrate materials', 'Group cards', 'Matching activities']
        },
        {
          week: 3,
          topic: 'Invertebrates',
          strand: 'Living Things',
          learningOutcomes: ['Define invertebrates', 'Identify invertebrate groups', 'Compare to vertebrates'],
          activities: ['Invertebrate study', 'Group identification', 'Comparison activities'],
          resources: ['Invertebrate materials', 'Group cards', 'Comparison charts']
        },
        {
          week: 4,
          topic: 'Plant Classification',
          strand: 'Living Things',
          learningOutcomes: ['Classify plants', 'Know flowering and non-flowering', 'Identify plant types'],
          activities: ['Plant classification', 'Type identification', 'Collection activities'],
          resources: ['Plant samples', 'Classification guides', 'Collection materials']
        },
        {
          week: 5,
          topic: 'Biodiversity',
          strand: 'Living Things',
          learningOutcomes: ['Understand biodiversity', 'Know diversity importance', 'Appreciate variety'],
          activities: ['Biodiversity exploration', 'Importance discussion', 'Diversity appreciation'],
          resources: ['Biodiversity materials', 'Discussion guides', 'Appreciation activities']
        },
        {
          week: 6,
          topic: 'Endangered Species',
          strand: 'Living Things',
          learningOutcomes: ['Know endangered species', 'Understand extinction causes', 'Learn protection methods'],
          activities: ['Endangered study', 'Cause exploration', 'Protection planning'],
          resources: ['Endangered species materials', 'Cause cards', 'Planning guides']
        },
        {
          week: 7,
          topic: 'Vanuatu Biodiversity',
          strand: 'Living Things',
          learningOutcomes: ['Know Vanuatu species', 'Appreciate local biodiversity', 'Understand conservation'],
          activities: ['Local species study', 'Appreciation activities', 'Conservation discussion'],
          resources: ['Local species guides', 'Appreciation materials', 'Discussion guides']
        },
        {
          week: 8,
          topic: 'Animal Behaviors',
          strand: 'Living Things',
          learningOutcomes: ['Observe animal behaviors', 'Understand behavior purposes', 'Record observations'],
          activities: ['Behavior observation', 'Purpose discussion', 'Observation recording'],
          resources: ['Observation materials', 'Discussion guides', 'Recording sheets']
        },
        {
          week: 9,
          topic: 'Classification Review',
          strand: 'Living Things',
          learningOutcomes: ['Review classification concepts', 'Apply classification skills', 'Demonstrate understanding'],
          activities: ['Review activities', 'Classification practice', 'Assessment preparation'],
          resources: ['Review materials', 'Practice activities', 'Preparation guides']
        },
        {
          week: 10,
          topic: 'Term 1 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate classification knowledge', 'Show biodiversity understanding', 'Complete assessments'],
          activities: ['Assessment tasks', 'Classification projects', 'Celebration'],
          resources: ['Assessment materials', 'Project supplies', 'Rewards']
        }
      ]
    },
    {
      term: 'Term 2',
      weeks: 10,
      focus: 'Electricity & Magnetism',
      scheme: [
        {
          week: 1,
          topic: 'Static Electricity',
          strand: 'Physical Science',
          learningOutcomes: ['Understand static electricity', 'Observe static effects', 'Know charge attraction/repulsion'],
          activities: ['Static experiments', 'Effect observation', 'Charge exploration'],
          resources: ['Static materials', 'Observation guides', 'Experiment supplies']
        },
        {
          week: 2,
          topic: 'Electric Circuits',
          strand: 'Physical Science',
          learningOutcomes: ['Understand circuit components', 'Build simple circuits', 'Know circuit requirements'],
          activities: ['Circuit building', 'Component identification', 'Circuit testing'],
          resources: ['Circuit materials', 'Component guides', 'Testing equipment']
        },
        {
          week: 3,
          topic: 'Conductors and Insulators',
          strand: 'Physical Science',
          learningOutcomes: ['Distinguish conductors and insulators', 'Test materials', 'Know applications'],
          activities: ['Material testing', 'Classification activities', 'Application discussion'],
          resources: ['Test materials', 'Circuit equipment', 'Classification charts']
        },
        {
          week: 4,
          topic: 'Series and Parallel Circuits',
          strand: 'Physical Science',
          learningOutcomes: ['Build series circuits', 'Build parallel circuits', 'Compare circuit types'],
          activities: ['Series circuit building', 'Parallel circuit building', 'Comparison activities'],
          resources: ['Circuit materials', 'Building guides', 'Comparison charts']
        },
        {
          week: 5,
          topic: 'Magnets',
          strand: 'Physical Science',
          learningOutcomes: ['Understand magnet properties', 'Know magnetic poles', 'Observe attraction/repulsion'],
          activities: ['Magnet exploration', 'Pole identification', 'Attraction experiments'],
          resources: ['Magnets', 'Exploration materials', 'Experiment supplies']
        },
        {
          week: 6,
          topic: 'Magnetic Materials',
          strand: 'Physical Science',
          learningOutcomes: ['Identify magnetic materials', 'Test materials', 'Classify by magnetism'],
          activities: ['Material testing', 'Classification activities', 'Result recording'],
          resources: ['Test materials', 'Magnets', 'Recording sheets']
        },
        {
          week: 7,
          topic: 'Electromagnets',
          strand: 'Physical Science',
          learningOutcomes: ['Understand electromagnets', 'Build simple electromagnet', 'Know applications'],
          activities: ['Electromagnet building', 'Testing activities', 'Application discussion'],
          resources: ['Building materials', 'Testing supplies', 'Discussion guides']
        },
        {
          week: 8,
          topic: 'Electricity Safety',
          strand: 'Physical Science',
          learningOutcomes: ['Know electricity dangers', 'Practice electrical safety', 'Identify safe behaviors'],
          activities: ['Safety discussion', 'Safe behavior identification', 'Safety planning'],
          resources: ['Safety materials', 'Behavior cards', 'Planning guides']
        },
        {
          week: 9,
          topic: 'Electricity & Magnetism Review',
          strand: 'Physical Science',
          learningOutcomes: ['Review electricity concepts', 'Review magnetism concepts', 'Demonstrate understanding'],
          activities: ['Review activities', 'Circuit building', 'Magnet experiments'],
          resources: ['Review materials', 'Circuit supplies', 'Magnet materials']
        },
        {
          week: 10,
          topic: 'Term 2 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Show electricity knowledge', 'Demonstrate magnetism understanding', 'Complete assessments'],
          activities: ['Assessment tasks', 'Practical tests', 'Celebration'],
          resources: ['Assessment materials', 'Practical supplies', 'Rewards']
        }
      ]
    },
    {
      term: 'Term 3',
      weeks: 10,
      focus: 'Earth Science & Weather',
      scheme: [
        {
          week: 1,
          topic: 'Rock Types',
          strand: 'Earth & Space',
          learningOutcomes: ['Identify rock types', 'Know rock formation', 'Classify rocks'],
          activities: ['Rock identification', 'Formation study', 'Classification activities'],
          resources: ['Rock samples', 'Formation guides', 'Classification charts']
        },
        {
          week: 2,
          topic: 'Rock Cycle',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand rock cycle', 'Know cycle stages', 'Model rock cycle'],
          activities: ['Cycle study', 'Stage identification', 'Cycle modeling'],
          resources: ['Cycle diagrams', 'Stage cards', 'Modeling materials']
        },
        {
          week: 3,
          topic: 'Weathering and Erosion',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand weathering types', 'Know erosion processes', 'Observe effects'],
          activities: ['Weathering study', 'Erosion observation', 'Effect documentation'],
          resources: ['Weathering materials', 'Observation sites', 'Documentation tools']
        },
        {
          week: 4,
          topic: 'Soil Formation',
          strand: 'Earth & Space',
          learningOutcomes: ['Know soil forms from rock', 'Identify soil layers', 'Understand soil importance'],
          activities: ['Soil study', 'Layer identification', 'Importance discussion'],
          resources: ['Soil samples', 'Layer diagrams', 'Discussion guides']
        },
        {
          week: 5,
          topic: 'Weather Instruments',
          strand: 'Earth & Space',
          learningOutcomes: ['Use weather instruments', 'Measure weather conditions', 'Record weather data'],
          activities: ['Instrument use', 'Weather measurement', 'Data recording'],
          resources: ['Weather instruments', 'Measurement guides', 'Recording sheets']
        },
        {
          week: 6,
          topic: 'Weather Patterns',
          strand: 'Earth & Space',
          learningOutcomes: ['Identify weather patterns', 'Understand pattern causes', 'Predict weather'],
          activities: ['Pattern identification', 'Cause study', 'Weather prediction'],
          resources: ['Weather data', 'Pattern guides', 'Prediction activities']
        },
        {
          week: 7,
          topic: 'Climate',
          strand: 'Earth & Space',
          learningOutcomes: ['Distinguish weather and climate', 'Know climate factors', 'Understand climate zones'],
          activities: ['Climate study', 'Factor identification', 'Zone exploration'],
          resources: ['Climate materials', 'Factor cards', 'Zone maps']
        },
        {
          week: 8,
          topic: 'Natural Disasters',
          strand: 'Earth & Space',
          learningOutcomes: ['Know natural disaster types', 'Understand causes', 'Learn safety measures'],
          activities: ['Disaster study', 'Cause exploration', 'Safety planning'],
          resources: ['Disaster materials', 'Cause guides', 'Safety plans']
        },
        {
          week: 9,
          topic: 'Earth Science Review',
          strand: 'Earth & Space',
          learningOutcomes: ['Review rocks and soil', 'Review weather concepts', 'Demonstrate understanding'],
          activities: ['Review activities', 'Weather observation', 'Rock classification'],
          resources: ['Review materials', 'Weather tools', 'Rock samples']
        },
        {
          week: 10,
          topic: 'Term 3 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Show earth science knowledge', 'Demonstrate weather understanding', 'Complete assessments'],
          activities: ['Assessment tasks', 'Practical observations', 'Celebration'],
          resources: ['Assessment materials', 'Observation tools', 'Rewards']
        }
      ]
    },
    {
      term: 'Term 4',
      weeks: 10,
      focus: 'Scientific Method & Investigation',
      scheme: [
        {
          week: 1,
          topic: 'Scientific Method Steps',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Know scientific method steps', 'Apply method to questions', 'Follow systematic process'],
          activities: ['Method introduction', 'Step practice', 'Process application'],
          resources: ['Method charts', 'Practice activities', 'Application guides']
        },
        {
          week: 2,
          topic: 'Asking Questions and Hypotheses',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Ask testable questions', 'Write hypotheses', 'Plan investigations'],
          activities: ['Question writing', 'Hypothesis formation', 'Investigation planning'],
          resources: ['Question guides', 'Hypothesis frames', 'Planning sheets']
        },
        {
          week: 3,
          topic: 'Variables',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Identify variable types', 'Control variables', 'Design fair tests'],
          activities: ['Variable identification', 'Control practice', 'Fair test design'],
          resources: ['Variable guides', 'Practice activities', 'Design templates']
        },
        {
          week: 4,
          topic: 'Conducting Experiments',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Follow procedures', 'Record observations', 'Collect data accurately'],
          activities: ['Experiment conducting', 'Observation recording', 'Data collection'],
          resources: ['Experiment materials', 'Recording sheets', 'Data tables']
        },
        {
          week: 5,
          topic: 'Analyzing Data',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Organize data', 'Create graphs', 'Identify patterns'],
          activities: ['Data organization', 'Graph creation', 'Pattern identification'],
          resources: ['Data sets', 'Graph materials', 'Pattern guides']
        },
        {
          week: 6,
          topic: 'Drawing Conclusions',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Analyze results', 'Draw conclusions', 'Evaluate hypotheses'],
          activities: ['Result analysis', 'Conclusion writing', 'Hypothesis evaluation'],
          resources: ['Analysis guides', 'Conclusion frames', 'Evaluation tools']
        },
        {
          week: 7,
          topic: 'Communicating Results',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Write reports', 'Present findings', 'Share discoveries'],
          activities: ['Report writing', 'Presentation creation', 'Sharing sessions'],
          resources: ['Report templates', 'Presentation materials', 'Sharing guides']
        },
        {
          week: 8,
          topic: 'Science Fair Preparation',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Plan science fair projects', 'Create displays', 'Prepare presentations'],
          activities: ['Project planning', 'Display creation', 'Presentation practice'],
          resources: ['Planning guides', 'Display materials', 'Practice checklists']
        },
        {
          week: 9,
          topic: 'Year 4 Science Review',
          strand: 'All Strands',
          learningOutcomes: ['Review all Year 4 topics', 'Connect concepts', 'Demonstrate understanding'],
          activities: ['Comprehensive review', 'Connection activities', 'Portfolio completion'],
          resources: ['Review materials', 'Connection tools', 'Portfolios']
        },
        {
          week: 10,
          topic: 'Year 4 Final Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Complete final assessment', 'Present science fair projects', 'Celebrate learning'],
          activities: ['Final assessment', 'Science fair', 'Celebration'],
          resources: ['Assessment materials', 'Fair displays', 'Celebration supplies']
        }
      ]
    }
  ]
};

// ============================================
// YEAR 5 SCIENCE CURRICULUM
// Exploring Systems
// ============================================
export const scienceYear5 = {
  year: 5,
  subject: 'Science',
  totalWeeks: 40,
  periodsPerWeek: 4,
  terms: [
    {
      term: 'Term 1',
      weeks: 10,
      focus: 'Human Body Systems',
      scheme: [
        {
          week: 1,
          topic: 'Nervous System',
          strand: 'Living Things',
          learningOutcomes: ['Understand nervous system function', 'Know brain and nerve roles', 'Connect to responses'],
          activities: ['Nervous system study', 'Response experiments', 'Brain exploration'],
          resources: ['Nervous system diagrams', 'Experiment materials', 'Brain models']
        },
        {
          week: 2,
          topic: 'Circulatory System Deep Dive',
          strand: 'Living Things',
          learningOutcomes: ['Understand blood circulation', 'Know heart structure', 'Measure pulse rate'],
          activities: ['Circulation study', 'Heart dissection/model', 'Pulse experiments'],
          resources: ['Heart models', 'Circulation diagrams', 'Pulse materials']
        },
        {
          week: 3,
          topic: 'Respiratory System Deep Dive',
          strand: 'Living Things',
          learningOutcomes: ['Understand gas exchange', 'Know lung structure', 'Measure breathing rate'],
          activities: ['Gas exchange study', 'Lung model', 'Breathing experiments'],
          resources: ['Lung models', 'Gas exchange diagrams', 'Experiment materials']
        },
        {
          week: 4,
          topic: 'Digestive System Deep Dive',
          strand: 'Living Things',
          learningOutcomes: ['Understand digestion stages', 'Know organ functions', 'Trace nutrient journey'],
          activities: ['Digestion study', 'Organ function matching', 'Nutrient tracing'],
          resources: ['Digestive models', 'Function cards', 'Tracing activities']
        },
        {
          week: 5,
          topic: 'Excretory System',
          strand: 'Living Things',
          learningOutcomes: ['Understand waste removal', 'Know kidney function', 'Connect to health'],
          activities: ['Excretory study', 'Kidney exploration', 'Health discussion'],
          resources: ['Excretory diagrams', 'Kidney models', 'Health guides']
        },
        {
          week: 6,
          topic: 'Reproductive System',
          strand: 'Living Things',
          learningOutcomes: ['Understand reproduction basics', 'Know life cycle stages', 'Understand growth'],
          activities: ['Reproduction introduction', 'Life cycle review', 'Growth discussion'],
          resources: ['Age-appropriate materials', 'Life cycle charts', 'Growth guides']
        },
        {
          week: 7,
          topic: 'Body Systems Working Together',
          strand: 'Living Things',
          learningOutcomes: ['Understand system interactions', 'Know systems depend on each other', 'Analyze connections'],
          activities: ['Interaction study', 'Connection mapping', 'Analysis activities'],
          resources: ['Interaction diagrams', 'Mapping materials', 'Analysis guides']
        },
        {
          week: 8,
          topic: 'Health and Lifestyle',
          strand: 'Living Things',
          learningOutcomes: ['Connect lifestyle to health', 'Make healthy choices', 'Understand disease prevention'],
          activities: ['Lifestyle analysis', 'Choice planning', 'Prevention discussion'],
          resources: ['Health materials', 'Planning guides', 'Prevention information']
        },
        {
          week: 9,
          topic: 'Body Systems Review',
          strand: 'Living Things',
          learningOutcomes: ['Review all body systems', 'Connect system functions', 'Demonstrate understanding'],
          activities: ['System review', 'Connection activities', 'Assessment preparation'],
          resources: ['Review materials', 'Connection charts', 'Preparation guides']
        },
        {
          week: 10,
          topic: 'Term 1 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate body system knowledge', 'Show understanding of interactions', 'Complete assessments'],
          activities: ['Assessment tasks', 'Body system projects', 'Celebration'],
          resources: ['Assessment materials', 'Project supplies', 'Rewards']
        }
      ]
    },
    {
      term: 'Term 2',
      weeks: 10,
      focus: 'Chemistry Basics',
      scheme: [
        {
          week: 1,
          topic: 'Matter and Its Properties',
          strand: 'Physical Science',
          learningOutcomes: ['Define matter', 'Identify properties', 'Measure properties'],
          activities: ['Matter exploration', 'Property identification', 'Measurement activities'],
          resources: ['Various materials', 'Property cards', 'Measurement tools']
        },
        {
          week: 2,
          topic: 'Atoms and Molecules',
          strand: 'Physical Science',
          learningOutcomes: ['Understand atoms', 'Know molecules', 'Model atomic structure'],
          activities: ['Atom introduction', 'Molecule study', 'Model building'],
          resources: ['Atom models', 'Molecule kits', 'Building materials']
        },
        {
          week: 3,
          topic: 'Elements and Compounds',
          strand: 'Physical Science',
          learningOutcomes: ['Distinguish elements and compounds', 'Know common elements', 'Understand combinations'],
          activities: ['Element study', 'Compound exploration', 'Combination activities'],
          resources: ['Periodic table', 'Compound examples', 'Combination materials']
        },
        {
          week: 4,
          topic: 'Mixtures and Solutions',
          strand: 'Physical Science',
          learningOutcomes: ['Distinguish mixtures and solutions', 'Create mixtures and solutions', 'Separate mixtures'],
          activities: ['Mixture creation', 'Solution making', 'Separation experiments'],
          resources: ['Mixing materials', 'Solution supplies', 'Separation equipment']
        },
        {
          week: 5,
          topic: 'Physical Changes',
          strand: 'Physical Science',
          learningOutcomes: ['Identify physical changes', 'Know reversibility', 'Observe examples'],
          activities: ['Change observation', 'Reversibility testing', 'Example identification'],
          resources: ['Change materials', 'Testing supplies', 'Example cards']
        },
        {
          week: 6,
          topic: 'Chemical Changes',
          strand: 'Physical Science',
          learningOutcomes: ['Identify chemical changes', 'Know indicators', 'Compare to physical changes'],
          activities: ['Chemical change experiments', 'Indicator identification', 'Comparison activities'],
          resources: ['Experiment materials', 'Indicator guides', 'Comparison charts']
        },
        {
          week: 7,
          topic: 'Acids and Bases',
          strand: 'Physical Science',
          learningOutcomes: ['Understand acids and bases', 'Test pH', 'Know common examples'],
          activities: ['pH testing', 'Acid/base identification', 'Example exploration'],
          resources: ['pH indicators', 'Test materials', 'Example supplies']
        },
        {
          week: 8,
          topic: 'Chemical Reactions in Life',
          strand: 'Physical Science',
          learningOutcomes: ['Know everyday reactions', 'Understand reaction importance', 'Identify examples'],
          activities: ['Everyday reaction study', 'Importance discussion', 'Example identification'],
          resources: ['Reaction examples', 'Discussion guides', 'Identification materials']
        },
        {
          week: 9,
          topic: 'Chemistry Review',
          strand: 'Physical Science',
          learningOutcomes: ['Review chemistry concepts', 'Connect learning', 'Demonstrate understanding'],
          activities: ['Concept review', 'Connection activities', 'Portfolio work'],
          resources: ['Review materials', 'Connection charts', 'Portfolios']
        },
        {
          week: 10,
          topic: 'Term 2 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Show chemistry knowledge', 'Demonstrate practical skills', 'Complete assessments'],
          activities: ['Assessment tasks', 'Practical tests', 'Celebration'],
          resources: ['Assessment materials', 'Practical supplies', 'Rewards']
        }
      ]
    },
    {
      term: 'Term 3',
      weeks: 10,
      focus: 'Earth & Space Science',
      scheme: [
        {
          week: 1,
          topic: 'Earth\'s Structure',
          strand: 'Earth & Space',
          learningOutcomes: ['Know Earth\'s layers', 'Understand layer properties', 'Model Earth structure'],
          activities: ['Layer study', 'Property exploration', 'Model creation'],
          resources: ['Layer diagrams', 'Property cards', 'Model materials']
        },
        {
          week: 2,
          topic: 'Plate Tectonics',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand plate movement', 'Know tectonic effects', 'Model plate movement'],
          activities: ['Plate study', 'Effect exploration', 'Movement modeling'],
          resources: ['Plate diagrams', 'Effect materials', 'Model supplies']
        },
        {
          week: 3,
          topic: 'Volcanoes',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand volcano formation', 'Know volcano types', 'Model eruptions'],
          activities: ['Volcano study', 'Type identification', 'Eruption modeling'],
          resources: ['Volcano materials', 'Type cards', 'Model supplies']
        },
        {
          week: 4,
          topic: 'Earthquakes',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand earthquake causes', 'Know earthquake effects', 'Learn safety measures'],
          activities: ['Earthquake study', 'Effect exploration', 'Safety planning'],
          resources: ['Earthquake materials', 'Effect pictures', 'Safety guides']
        },
        {
          week: 5,
          topic: 'Solar System',
          strand: 'Earth & Space',
          learningOutcomes: ['Know solar system components', 'Understand planet order', 'Model solar system'],
          activities: ['Solar system study', 'Planet ordering', 'System modeling'],
          resources: ['Solar system materials', 'Planet cards', 'Model supplies']
        },
        {
          week: 6,
          topic: 'Earth\'s Movement',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand rotation and revolution', 'Know effects on day/night and seasons', 'Model movements'],
          activities: ['Movement study', 'Effect exploration', 'Movement modeling'],
          resources: ['Movement diagrams', 'Effect materials', 'Model supplies']
        },
        {
          week: 7,
          topic: 'Moon Phases',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand moon phases', 'Know phase cycle', 'Observe moon'],
          activities: ['Phase study', 'Cycle learning', 'Moon observation'],
          resources: ['Phase diagrams', 'Cycle charts', 'Observation guides']
        },
        {
          week: 8,
          topic: 'Stars and Constellations',
          strand: 'Earth & Space',
          learningOutcomes: ['Know star characteristics', 'Identify constellations', 'Understand starlight'],
          activities: ['Star study', 'Constellation identification', 'Starlight exploration'],
          resources: ['Star charts', 'Constellation guides', 'Exploration materials']
        },
        {
          week: 9,
          topic: 'Earth & Space Review',
          strand: 'Earth & Space',
          learningOutcomes: ['Review Earth science', 'Review space concepts', 'Demonstrate understanding'],
          activities: ['Concept review', 'Space exploration', 'Portfolio work'],
          resources: ['Review materials', 'Space resources', 'Portfolios']
        },
        {
          week: 10,
          topic: 'Term 3 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Show Earth science knowledge', 'Demonstrate space understanding', 'Complete assessments'],
          activities: ['Assessment tasks', 'Space projects', 'Celebration'],
          resources: ['Assessment materials', 'Project supplies', 'Rewards']
        }
      ]
    },
    {
      term: 'Term 4',
      weeks: 10,
      focus: 'Energy & Technology',
      scheme: [
        {
          week: 1,
          topic: 'Forms of Energy',
          strand: 'Physical Science',
          learningOutcomes: ['Identify energy forms', 'Know energy uses', 'Classify energy'],
          activities: ['Energy identification', 'Use exploration', 'Classification activities'],
          resources: ['Energy materials', 'Use examples', 'Classification charts']
        },
        {
          week: 2,
          topic: 'Energy Transformation',
          strand: 'Physical Science',
          learningOutcomes: ['Understand energy transformation', 'Identify transformations', 'Trace energy changes'],
          activities: ['Transformation study', 'Identification activities', 'Energy tracing'],
          resources: ['Transformation diagrams', 'Identification materials', 'Tracing activities']
        },
        {
          week: 3,
          topic: 'Renewable Energy',
          strand: 'Physical Science',
          learningOutcomes: ['Know renewable sources', 'Understand benefits', 'Explore technologies'],
          activities: ['Source study', 'Benefit discussion', 'Technology exploration'],
          resources: ['Source materials', 'Benefit cards', 'Technology examples']
        },
        {
          week: 4,
          topic: 'Non-Renewable Energy',
          strand: 'Physical Science',
          learningOutcomes: ['Know non-renewable sources', 'Understand limitations', 'Compare to renewable'],
          activities: ['Source study', 'Limitation discussion', 'Comparison activities'],
          resources: ['Source materials', 'Discussion guides', 'Comparison charts']
        },
        {
          week: 5,
          topic: 'Energy Conservation',
          strand: 'Physical Science',
          learningOutcomes: ['Understand conservation need', 'Know conservation methods', 'Plan conservation actions'],
          activities: ['Need discussion', 'Method exploration', 'Action planning'],
          resources: ['Conservation materials', 'Method cards', 'Planning guides']
        },
        {
          week: 6,
          topic: 'Technology and Science',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Connect technology and science', 'Know technology benefits', 'Explore innovations'],
          activities: ['Connection study', 'Benefit discussion', 'Innovation exploration'],
          resources: ['Technology examples', 'Discussion guides', 'Innovation materials']
        },
        {
          week: 7,
          topic: 'Design Process',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Know design process steps', 'Apply process', 'Create solutions'],
          activities: ['Process introduction', 'Application practice', 'Solution creation'],
          resources: ['Process charts', 'Practice materials', 'Creation supplies']
        },
        {
          week: 8,
          topic: 'Engineering Challenges',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Complete engineering challenges', 'Apply scientific knowledge', 'Solve problems'],
          activities: ['Challenge completion', 'Knowledge application', 'Problem solving'],
          resources: ['Challenge materials', 'Application guides', 'Problem supplies']
        },
        {
          week: 9,
          topic: 'Year 5 Science Review',
          strand: 'All Strands',
          learningOutcomes: ['Review all Year 5 topics', 'Connect concepts', 'Demonstrate understanding'],
          activities: ['Comprehensive review', 'Connection activities', 'Portfolio completion'],
          resources: ['Review materials', 'Connection charts', 'Portfolios']
        },
        {
          week: 10,
          topic: 'Year 5 Final Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Complete final assessment', 'Present projects', 'Celebrate learning'],
          activities: ['Final assessment', 'Project presentations', 'Celebration'],
          resources: ['Assessment materials', 'Presentation supplies', 'Celebration supplies']
        }
      ]
    }
  ]
};

// ============================================
// YEAR 6 SCIENCE CURRICULUM
// Preparing for Secondary Science
// ============================================
export const scienceYear6 = {
  year: 6,
  subject: 'Science',
  totalWeeks: 40,
  periodsPerWeek: 5,
  terms: [
    {
      term: 'Term 1',
      weeks: 10,
      focus: 'Cells & Genetics',
      scheme: [
        {
          week: 1,
          topic: 'Cell Theory',
          strand: 'Living Things',
          learningOutcomes: ['Understand cell theory', 'Know all living things have cells', 'Appreciate cell discovery'],
          activities: ['Cell theory study', 'History exploration', 'Theory application'],
          resources: ['Cell theory materials', 'History resources', 'Application activities']
        },
        {
          week: 2,
          topic: 'Cell Structure',
          strand: 'Living Things',
          learningOutcomes: ['Identify cell parts', 'Know cell part functions', 'Compare plant and animal cells'],
          activities: ['Cell observation', 'Part identification', 'Cell comparison'],
          resources: ['Microscopes', 'Cell diagrams', 'Comparison charts']
        },
        {
          week: 3,
          topic: 'Using Microscopes',
          strand: 'Living Things',
          learningOutcomes: ['Use microscopes correctly', 'Prepare slides', 'Observe specimens'],
          activities: ['Microscope training', 'Slide preparation', 'Specimen observation'],
          resources: ['Microscopes', 'Slide materials', 'Specimens']
        },
        {
          week: 4,
          topic: 'Cell Processes',
          strand: 'Living Things',
          learningOutcomes: ['Understand cell processes', 'Know respiration basics', 'Understand photosynthesis'],
          activities: ['Process study', 'Respiration exploration', 'Photosynthesis experiments'],
          resources: ['Process diagrams', 'Experiment materials', 'Exploration guides']
        },
        {
          week: 5,
          topic: 'Heredity Introduction',
          strand: 'Living Things',
          learningOutcomes: ['Understand heredity', 'Know traits are inherited', 'Identify inherited traits'],
          activities: ['Heredity introduction', 'Trait identification', 'Family trait study'],
          resources: ['Heredity materials', 'Trait cards', 'Study guides']
        },
        {
          week: 6,
          topic: 'Genes and DNA',
          strand: 'Living Things',
          learningOutcomes: ['Know genes carry information', 'Understand DNA basics', 'Model DNA structure'],
          activities: ['Gene study', 'DNA exploration', 'DNA modeling'],
          resources: ['Gene materials', 'DNA models', 'Modeling supplies']
        },
        {
          week: 7,
          topic: 'Variation',
          strand: 'Living Things',
          learningOutcomes: ['Understand variation', 'Know causes of variation', 'Observe variation examples'],
          activities: ['Variation study', 'Cause exploration', 'Example observation'],
          resources: ['Variation materials', 'Cause guides', 'Observation activities']
        },
        {
          week: 8,
          topic: 'Evolution Introduction',
          strand: 'Living Things',
          learningOutcomes: ['Understand evolution basics', 'Know natural selection', 'Appreciate adaptation'],
          activities: ['Evolution introduction', 'Selection study', 'Adaptation exploration'],
          resources: ['Evolution materials', 'Selection examples', 'Adaptation guides']
        },
        {
          week: 9,
          topic: 'Cells & Genetics Review',
          strand: 'Living Things',
          learningOutcomes: ['Review cell concepts', 'Review genetics basics', 'Demonstrate understanding'],
          activities: ['Concept review', 'Genetics review', 'Assessment preparation'],
          resources: ['Review materials', 'Genetics resources', 'Preparation guides']
        },
        {
          week: 10,
          topic: 'Term 1 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate cell knowledge', 'Show genetics understanding', 'Complete assessments'],
          activities: ['Assessment tasks', 'Practical tests', 'Celebration'],
          resources: ['Assessment materials', 'Practical supplies', 'Rewards']
        }
      ]
    },
    {
      term: 'Term 2',
      weeks: 10,
      focus: 'Physics Foundations',
      scheme: [
        {
          week: 1,
          topic: 'Motion and Speed',
          strand: 'Physical Science',
          learningOutcomes: ['Measure motion', 'Calculate speed', 'Graph motion'],
          activities: ['Motion measurement', 'Speed calculation', 'Graph creation'],
          resources: ['Motion materials', 'Calculation tools', 'Graphing supplies']
        },
        {
          week: 2,
          topic: 'Acceleration',
          strand: 'Physical Science',
          learningOutcomes: ['Understand acceleration', 'Measure acceleration', 'Calculate acceleration'],
          activities: ['Acceleration study', 'Measurement activities', 'Calculation practice'],
          resources: ['Acceleration materials', 'Measurement tools', 'Calculation guides']
        },
        {
          week: 3,
          topic: 'Newton\'s Laws',
          strand: 'Physical Science',
          learningOutcomes: ['Know Newton\'s three laws', 'Apply laws to situations', 'Demonstrate laws'],
          activities: ['Law study', 'Application activities', 'Demonstration experiments'],
          resources: ['Law materials', 'Application guides', 'Experiment supplies']
        },
        {
          week: 4,
          topic: 'Work and Energy',
          strand: 'Physical Science',
          learningOutcomes: ['Understand work concept', 'Calculate work', 'Relate work to energy'],
          activities: ['Work study', 'Calculation practice', 'Energy connection'],
          resources: ['Work materials', 'Calculation guides', 'Energy resources']
        },
        {
          week: 5,
          topic: 'Potential and Kinetic Energy',
          strand: 'Physical Science',
          learningOutcomes: ['Distinguish energy types', 'Calculate energy', 'Observe energy transformation'],
          activities: ['Energy distinction', 'Calculation practice', 'Transformation observation'],
          resources: ['Energy materials', 'Calculation tools', 'Observation activities']
        },
        {
          week: 6,
          topic: 'Light Properties',
          strand: 'Physical Science',
          learningOutcomes: ['Know light properties', 'Understand reflection and refraction', 'Experiment with light'],
          activities: ['Property study', 'Reflection/refraction experiments', 'Light exploration'],
          resources: ['Light materials', 'Experiment supplies', 'Exploration guides']
        },
        {
          week: 7,
          topic: 'Sound Properties',
          strand: 'Physical Science',
          learningOutcomes: ['Know sound properties', 'Understand sound waves', 'Measure sound'],
          activities: ['Property study', 'Wave exploration', 'Sound measurement'],
          resources: ['Sound materials', 'Wave models', 'Measurement tools']
        },
        {
          week: 8,
          topic: 'Waves',
          strand: 'Physical Science',
          learningOutcomes: ['Understand wave properties', 'Know wave types', 'Model waves'],
          activities: ['Property study', 'Type identification', 'Wave modeling'],
          resources: ['Wave materials', 'Type guides', 'Modeling supplies']
        },
        {
          week: 9,
          topic: 'Physics Review',
          strand: 'Physical Science',
          learningOutcomes: ['Review motion concepts', 'Review energy concepts', 'Demonstrate understanding'],
          activities: ['Motion review', 'Energy review', 'Portfolio work'],
          resources: ['Review materials', 'Energy resources', 'Portfolios']
        },
        {
          week: 10,
          topic: 'Term 2 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Show physics knowledge', 'Demonstrate calculation skills', 'Complete assessments'],
          activities: ['Assessment tasks', 'Calculation tests', 'Celebration'],
          resources: ['Assessment materials', 'Calculation supplies', 'Rewards']
        }
      ]
    },
    {
      term: 'Term 3',
      weeks: 10,
      focus: 'Environmental Science',
      scheme: [
        {
          week: 1,
          topic: 'Biomes',
          strand: 'Earth & Space',
          learningOutcomes: ['Know major biomes', 'Understand biome characteristics', 'Compare biomes'],
          activities: ['Biome study', 'Characteristic exploration', 'Biome comparison'],
          resources: ['Biome materials', 'Characteristic cards', 'Comparison charts']
        },
        {
          week: 2,
          topic: 'Pacific Island Ecosystems',
          strand: 'Earth & Space',
          learningOutcomes: ['Know Pacific ecosystems', 'Understand island ecology', 'Appreciate local biodiversity'],
          activities: ['Ecosystem study', 'Ecology exploration', 'Biodiversity appreciation'],
          resources: ['Pacific materials', 'Ecology guides', 'Local resources']
        },
        {
          week: 3,
          topic: 'Climate Change',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand climate change', 'Know causes and effects', 'Learn mitigation'],
          activities: ['Climate change study', 'Cause/effect exploration', 'Mitigation planning'],
          resources: ['Climate materials', 'Cause/effect cards', 'Planning guides']
        },
        {
          week: 4,
          topic: 'Ocean Conservation',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand ocean importance', 'Know ocean threats', 'Plan conservation actions'],
          activities: ['Ocean study', 'Threat exploration', 'Conservation planning'],
          resources: ['Ocean materials', 'Threat cards', 'Planning guides']
        },
        {
          week: 5,
          topic: 'Forest Conservation',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand forest importance', 'Know deforestation effects', 'Plan protection'],
          activities: ['Forest study', 'Effect exploration', 'Protection planning'],
          resources: ['Forest materials', 'Effect guides', 'Planning resources']
        },
        {
          week: 6,
          topic: 'Sustainable Development',
          strand: 'Earth & Space',
          learningOutcomes: ['Understand sustainability', 'Know sustainable practices', 'Plan sustainable actions'],
          activities: ['Sustainability study', 'Practice exploration', 'Action planning'],
          resources: ['Sustainability materials', 'Practice cards', 'Planning guides']
        },
        {
          week: 7,
          topic: 'Environmental Careers',
          strand: 'Earth & Space',
          learningOutcomes: ['Know environmental careers', 'Understand career requirements', 'Explore interests'],
          activities: ['Career exploration', 'Requirement study', 'Interest identification'],
          resources: ['Career materials', 'Requirement guides', 'Interest assessments']
        },
        {
          week: 8,
          topic: 'Community Action Project',
          strand: 'Earth & Space',
          learningOutcomes: ['Plan community project', 'Implement action', 'Evaluate impact'],
          activities: ['Project planning', 'Action implementation', 'Impact evaluation'],
          resources: ['Project guides', 'Implementation materials', 'Evaluation tools']
        },
        {
          week: 9,
          topic: 'Environmental Science Review',
          strand: 'Earth & Space',
          learningOutcomes: ['Review environmental concepts', 'Connect learning', 'Demonstrate understanding'],
          activities: ['Concept review', 'Connection activities', 'Portfolio completion'],
          resources: ['Review materials', 'Connection charts', 'Portfolios']
        },
        {
          week: 10,
          topic: 'Term 3 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Show environmental knowledge', 'Present projects', 'Complete assessments'],
          activities: ['Assessment tasks', 'Project presentations', 'Celebration'],
          resources: ['Assessment materials', 'Presentation supplies', 'Rewards']
        }
      ]
    },
    {
      term: 'Term 4',
      weeks: 10,
      focus: 'Scientific Investigation & Secondary Preparation',
      scheme: [
        {
          week: 1,
          topic: 'Advanced Scientific Method',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Apply advanced methods', 'Design controlled experiments', 'Analyze complex data'],
          activities: ['Method application', 'Experiment design', 'Data analysis'],
          resources: ['Method guides', 'Design templates', 'Analysis tools']
        },
        {
          week: 2,
          topic: 'Research Skills',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Conduct scientific research', 'Evaluate sources', 'Synthesize information'],
          activities: ['Research practice', 'Source evaluation', 'Information synthesis'],
          resources: ['Research materials', 'Evaluation guides', 'Synthesis tools']
        },
        {
          week: 3,
          topic: 'Laboratory Skills',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Use lab equipment safely', 'Follow procedures', 'Record observations accurately'],
          activities: ['Equipment training', 'Procedure practice', 'Observation recording'],
          resources: ['Lab equipment', 'Procedure guides', 'Recording materials']
        },
        {
          week: 4,
          topic: 'Scientific Writing',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Write lab reports', 'Use scientific format', 'Communicate findings'],
          activities: ['Report writing', 'Format practice', 'Communication activities'],
          resources: ['Report templates', 'Format guides', 'Communication materials']
        },
        {
          week: 5,
          topic: 'Extended Investigation',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Conduct extended investigation', 'Manage long-term project', 'Present findings'],
          activities: ['Investigation conducting', 'Project management', 'Finding presentation'],
          resources: ['Investigation materials', 'Management tools', 'Presentation supplies']
        },
        {
          week: 6,
          topic: 'Secondary Science Preview',
          strand: 'All Strands',
          learningOutcomes: ['Preview secondary topics', 'Identify readiness', 'Set learning goals'],
          activities: ['Topic preview', 'Readiness assessment', 'Goal setting'],
          resources: ['Preview materials', 'Assessment tools', 'Goal worksheets']
        },
        {
          week: 7,
          topic: 'Science Fair Project',
          strand: 'Scientific Inquiry',
          learningOutcomes: ['Complete science fair project', 'Create display', 'Prepare presentation'],
          activities: ['Project completion', 'Display creation', 'Presentation preparation'],
          resources: ['Project materials', 'Display supplies', 'Presentation guides']
        },
        {
          week: 8,
          topic: 'Year 6 Science Review',
          strand: 'All Strands',
          learningOutcomes: ['Review all Year 6 topics', 'Connect concepts', 'Demonstrate mastery'],
          activities: ['Comprehensive review', 'Concept connection', 'Mastery demonstration'],
          resources: ['Review materials', 'Connection charts', 'Demonstration activities']
        },
        {
          week: 9,
          topic: 'Primary Science Celebration',
          strand: 'All Strands',
          learningOutcomes: ['Reflect on primary learning', 'Celebrate achievements', 'Appreciate science journey'],
          activities: ['Reflection activities', 'Achievement celebration', 'Journey appreciation'],
          resources: ['Reflection guides', 'Celebration supplies', 'Journey materials']
        },
        {
          week: 10,
          topic: 'Year 6 Final Assessment & Graduation',
          strand: 'All Strands',
          learningOutcomes: ['Complete final assessment', 'Present at science fair', 'Graduate primary science'],
          activities: ['Final assessment', 'Science fair', 'Graduation ceremony'],
          resources: ['Assessment materials', 'Fair displays', 'Graduation supplies']
        }
      ]
    }
  ]
};

export default {
  scienceYear1,
  scienceYear2,
  scienceYear3,
  scienceYear4,
  scienceYear5,
  scienceYear6
};
